import React from "react";

export const TEXT_PRIMARY = "#0b0b0b";
export const TEXT_SECONDARY = "#52514e";
export const GRID_STROKE = "#e5e5e2";

/** Kobo stores raw XML value names ("out_patient"); show something readable. */
export function humanize(value: string) {
  if (/[\s/]/.test(value)) return value;
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

/**
 * A label sitting inside a colored fill picks white or ink by the fill's luminance,
 * so it always clears contrast no matter which slot the series landed in.
 */
export function inkFor(fill: string) {
  const channel = (offset: number) => {
    const value = parseInt(fill.slice(offset, offset + 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  const luminance =
    0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
  return luminance > 0.42 ? TEXT_PRIMARY : "#ffffff";
}

/**
 * Renders a value inside its own stacked segment, but only when the text fits with
 * padding on both sides — a clipped number is worse than no number, and the tooltip
 * carries every value regardless.
 */
export function makeSegmentLabel(
  fill: string,
  format: (value: number) => string = (value) => `${value}`,
) {
  return (props: any) => {
    const { x, y, width, height, value } = props;
    if (value === undefined || value === null || value === 0) return null;

    const text = format(value);
    const needed = text.length * 8 + 14;
    if (width < needed || height < 16) return null;

    return (
      <text
        x={x + width / 2}
        y={y + height / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={12}
        fontWeight={600}
        fill={inkFor(fill)}
      >
        {text}
      </text>
    );
  };
}

/**
 * Organization names vary a lot in length; size the axis gutter to the longest one
 * (within bounds) so a long name is never clipped by the plot edge.
 */
export function yAxisWidthFor(labels: string[]) {
  const longest = labels.reduce((max, label) => Math.max(max, label.length), 0);
  return Math.min(240, Math.max(120, longest * 7 + 16));
}

/** The loading / syncing state shared by the analytics charts. */
export function ChartLoading({
  isSyncing,
  label,
}: {
  isSyncing: boolean;
  label: string;
}) {
  return (
    <div
      style={{
        textAlign: "center",
        padding: "2rem",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "0.75rem",
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          border: "3px solid #e0e0e0",
          borderTop: "3px solid #1976d2",
          borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
        }}
      />
      <div style={{ fontWeight: 500, color: "#333" }}>
        {isSyncing ? "Syncing data from Kobo…" : label}
      </div>
    </div>
  );
}

/**
 * Where a chart ends up when the sync did not produce data: it failed, or it outlasted
 * the polling budget. Both are dead ends for the spinner, so say which one it was and
 * offer the way out — retrying beats making the user reload the page to find out.
 */
export function ChartSyncNotice({
  state,
  message,
  onRetry,
}: {
  state: "failed" | "stalled";
  message?: string;
  onRetry: () => void;
}) {
  const failed = state === "failed";

  return (
    <div
      style={{
        textAlign: "center",
        padding: "2rem",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "0.5rem",
      }}
    >
      <div style={{ fontSize: 28, lineHeight: 1 }}>{failed ? "⚠️" : "⏳"}</div>
      <div style={{ fontWeight: 600, color: TEXT_PRIMARY }}>
        {failed ? "Couldn't sync this period" : "Still syncing"}
      </div>
      {message && (
        <div
          style={{
            color: TEXT_SECONDARY,
            fontSize: "0.8125rem",
            maxWidth: 420,
          }}
        >
          {message}
        </div>
      )}
      <button
        type="button"
        onClick={onRetry}
        style={{
          marginTop: "0.5rem",
          padding: "0.4rem 1.1rem",
          fontSize: "0.8125rem",
          fontWeight: 600,
          color: "#1565c0",
          background: "#e3f2fd",
          border: "1px solid #90caf9",
          borderRadius: 20,
          cursor: "pointer",
        }}
      >
        Retry
      </button>
    </div>
  );
}
